import { lazy, Suspense, useEffect } from 'react'
import { useMutation } from 'react-query'
import { useSearchParams } from 'react-router-dom'
import { PageHeader, Tab, TabList, Tabs } from '@clickguard/design-system'
import { accountOverviewApi } from 'services/account-overview/account-overview'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { GLazyLoading } from 'components/basic-blocks'

type AccountOverviewTab = 'websites' | 'workspaces'

// Code-split the two tab bodies (mirrors clicks/exclusions): each tab's bundle
// is fetched only when the user actually opens it, keeping the main chunk lean.
const WebsitesTab = lazy(() =>
	import('./websites-tab')
		.then((m) => ({ default: m.WebsitesTab }))
		.catch((error) => {
			console.error('Failed to load WebsitesTab:', error)
			return { default: () => <div>Error loading the component.....</div> }
		})
)
const WorkspacesTab = lazy(() =>
	import('./workspaces-tab')
		.then((m) => ({ default: m.WorkspacesTab }))
		.catch((error) => {
			console.error('Failed to load WorkspacesTab:', error)
			return { default: () => <div>Error loading the component.....</div> }
		})
)

/**
 * DEV-2339 · T3 — Account Overview page shell.
 *
 * Cross-workspace dashboard rendered inside the app shell (MainLayout → navbar +
 * sidebar), so the currently selected workspace stays selected. Two tabs —
 * Websites (default) and Workspaces — with empty bodies for now; T5 fills
 * Workspaces and T10 fills Websites.
 *
 * Opening by any route marks the discovery coachmark seen (T11 flag, drives T12).
 */
export const AccountOverviewPage = () => {
	// Active tab lives in the URL (deep-linkable, survives refresh). Switching tabs
	// resets the query string so the other tab's filters don't leak across.
	const [searchParams, setSearchParams] = useSearchParams()
	const tab = (searchParams.get('tab') as AccountOverviewTab) || 'websites'

	const { mutate: markIntroSeen } = useMutation(() =>
		accountOverviewApi.markIntroSeen()
	)
	const track = useTrackPostHogEvent()

	// Fire once on first open, by any route. Failure is non-blocking (coachmark
	// just shows again next session) — no error surfaced to the user.
	useEffect(() => {
		markIntroSeen()
		// T13 — pageview on open (tab from URL, default websites).
		track(
			PostHogEvent.ACCOUNT_OVERVIEW_PAGEVIEW,
			PostHogPath.ACCOUNT_OVERVIEW,
			{ tab }
		)
	}, [])

	// T13 — pageview also fires on every tab switch.
	const changeTab = (next: AccountOverviewTab) => {
		setSearchParams({ tab: next })
		track(
			PostHogEvent.ACCOUNT_OVERVIEW_PAGEVIEW,
			PostHogPath.ACCOUNT_OVERVIEW,
			{ tab: next }
		)
	}

	return (
		<div className="flex flex-col gap-3 p-4 pb-24">
			{/* Inline header: "Account Overview" │ subtitle (Figma 5777:248282). */}
			<PageHeader
				layout="inline"
				title="Account Overview"
				subtitle="Monitor all websites and ad accounts across all workspaces"
			/>

			{/* Underline tabs (Figma "Tab Group"): Websites (default) / Workspaces. */}
			<Tabs
				value={tab}
				onChange={(value) => changeTab(value as AccountOverviewTab)}
				size="sm"
			>
				<TabList fullBleed>
					<Tab value="websites" label="Websites" />
					<Tab value="workspaces" label="Workspaces" />
				</TabList>
			</Tabs>

			{/* Tab bodies — Websites (T10) / Workspaces (T5). Lazy-loaded: the
			    chunk fetches on first open, with a loading fallback meanwhile. */}
			<Suspense fallback={<GLazyLoading />}>
				{tab === 'websites' && <WebsitesTab />}
				{tab === 'workspaces' && <WorkspacesTab />}
			</Suspense>
		</div>
	)
}
